import {
  trackNewsClick,
  trackIndustryStudiesClick,
  trackAffordabilityRankingsClick,
} from 'lib/Tracking';
import { useTranslation } from 'react-i18next';
import TrustLogos from 'components/TrustLogos';
import Articles from 'components/ContentDashboard/Articles';
import RecentStudiesCarousel from 'components/RecentStudiesCarousel';
// styles
import { screen } from 'components/styles/variables';
// assets
import carGraph from 'public/static/images/car-graph.png';
import grayCurveTop from 'public/static/images/gray-curve-top.png';
import grayCurveBottom from 'public/static/images/gray-curve-bottom.png';
import rightArrow from 'public/static/images/right-arrow-red-small.png';
import useMakesAndModels from 'lib/hooks/useMakesAndModels';
import WindowLocation from 'lib/WindowLocation';
import { Infographic } from '../types/Apis/Infographic';

interface Props {
  articleSummaries: [];
  articleSummariesTotal: number;
}

function onSelectInfographic(infographic: Infographic, push: (url: string) => void): void {
  const clickUrl = infographic.click_url.slice(1); // strip leading slash
  push(clickUrl);
}

function ContentDashboard({ articleSummaries, articleSummariesTotal }: Props): JSX.Element {
  const { t } = useTranslation();
  const push = WindowLocation.assign;

  useMakesAndModels();
  return (
    <div className="content-dashboard">
      <Cover />
      <div className="container">
        <div className="row">
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
            <TrustLogos />
          </div>
        </div>
      </div>
      <Articles
        articleSummaries={articleSummaries}
        total={articleSummariesTotal}
        parentCategory={'NewsAndAnalysis'}
        title={t('contentDashboard.news')}
        rssUrl="https://www.autolist.com/api/articles/rss.xml"
      />
      <RecentStudiesCarousel
        onSelect={(val: Infographic): void => {
          onSelectInfographic(val, push);
        }}
      />
      <FooterBlurb />
      <ContactUs />
      <style jsx global>
        {`
          .content-dashboard {
            font-size: 19px;
            line-height: 25px;
            font-weight: 300;
          }

          .content-dashboard row > [class*='col-'] {
            padding-left: 15px !important;
            padding-right: 15px !important;
          }
        `}
      </style>
    </div>
  );
}

function Cover(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div>
      <div className="container">
        <div className="row">
          <Spacer height={50} />
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
            <div className="car-graph-hero">
              <h1 className="content-dashboard-title">{t('contentDashboard.contentTitle')}</h1>
              <Spacer height={15} />
              <div>{t('contentDashboard.contentSubtitle')}</div>
              <Spacer height={15} />
              <ul className="content-dashboard-cover-list">
                <li className="content-dashboard-cover-list-item">
                  <a
                    className="content-dashboard-cover-list-item-anchor"
                    href="#news"
                    onClick={trackNewsClick}
                  >
                    {t('contentDashboard.news')}
                    <div className="arrow" />
                  </a>
                </li>
                <li className="content-dashboard-cover-list-item">
                  <a
                    className="content-dashboard-cover-list-item-anchor"
                    href="#industry-studies"
                    onClick={trackIndustryStudiesClick}
                  >
                    {t('contentDashboard.industryStudies')}
                    <div className="arrow" />
                  </a>
                </li>
                <li className="content-dashboard-cover-list-item">
                  <a
                    className="content-dashboard-cover-list-item-anchor"
                    href="#affordability-rankings"
                    onClick={trackAffordabilityRankingsClick}
                  >
                    {t('contentDashboard.affordabilityRankings')}
                    <div className="arrow" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="gray-curve" />
      <style jsx>
        {`
          .car-graph-hero {
            background-image: url('${carGraph}');
            height: 350px;
            background-size: 518px 160px;
            background-position: 0 172px;
            background-repeat: repeat-x;
          }

          .content-dashboard-title {
            margin: 0;
            padding: 0;
            font-size: 43px;
            font-weight: bold;
            line-height: 40px;
          }

          @media (min-width: ${screen.smMin}) and (max-width: ${screen.smMax}) {
            .car-graph-hero {
              background-size: 926px 271px;
              background-position: 0 64px;
              background-repeat: repeat-x;
            }
          }

          @media (min-width: ${screen.mdMin}) and (max-width: ${screen.mdMax}) {
            .car-graph-hero {
              background-size: 1047px 286px;
              background-position: 0 45px;
            }
          }

          @media (min-width: ${screen.lgMin}) {
            .car-graph-hero {
              background-size: 1140px 310px;
              background-position: 0 26px;
            }
          }

          .content-dashboard-cover-list {
            margin: 0;
            padding: 0;
            list-style-type: none;
          }

          .content-dashboard-cover-list-item {
            display: block;
            padding-bottom: 8px;
          }

          @media (min-width: ${screen.smMin}) {
            .content-dashboard-cover-list-item {
              display: inline-block;
              padding: 0 20px 0 0;
            }

            li:last-child {
              padding-right: 0;
            }
          }

          .content-dashboard-cover-list-item-anchor {
            font-size: 18px;
          }

          .arrow {
            position: relative;
            display: inline-block;
            top: 1px;
            margin-left: 3px;
            height: 12px;
            width: 9px;
            background: url(${rightArrow});
            background-size: 9px 12px;
          }

          .gray-curve {
            width: 100%;
            height: 50px;
            background-size: cover;
            background-position: center center;
            margin-top: -20px;
            margin-left: 15px;
            background-image: url(${grayCurveTop});
          }
        `}
      </style>
    </div>
  );
}

function FooterBlurb(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <div className="gray-curve" />
      <div className="container">
        <div className="row">
          <div className="col-xs-12 col-sm-4 col-md-4 col-lg-4">
            <h3 className="content-dashboard-content-title">
              {t('contentDashboard.leftContentTitle')}
            </h3>
            <div className="hspacer-15px" />
            <div>{t('contentDashboard.leftContentText')}</div>
            <div className="hspacer-30px-xs" />
          </div>
          <div className="col-xs-12 col-sm-4 col-md-4 col-lg-4">
            <h3 className="content-dashboard-content-title">
              {t('contentDashboard.centerContentTitle')}
            </h3>
            <div className="hspacer-15px" />
            <div>{t('contentDashboard.centerContentText')}</div>
            <div className="hspacer-30px-xs" />
          </div>
          <div className="col-xs-12 col-sm-4 col-md-4 col-lg-4">
            <h3 className="content-dashboard-content-title">
              {t('contentDashboard.rightContentTitle')}
            </h3>
            <div className="hspacer-15px" />
            <div>{t('contentDashboard.rightContentText')}</div>
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .hspacer-15px {
            height: 15px;
          }

          @media (max-width: ${screen.xsMax}) {
            .hspacer-30px-xs {
              height: 30px;
            }
          }

          .gray-curve {
            width: 100%;
            height: 50px;
            background-size: cover;
            background-position: center center;
            margin: -20px 0 50px 0;
            background-image: url(${grayCurveBottom});
          }

          .content-dashboard-content-title {
            margin: 0;
            padding: 0;
            font-size: 24px;
            font-weight: bold;
            line-height: 22px;
          }
        `}
      </style>
    </div>
  );
}

function ContactUs(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="container">
      <div className="row">
        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
          <div className="contact-us">
            {t('contentDashboard.request')}
            <br className="visible-xs" />
            {t('contentDashboard.contactOur')}
            <a className="contact-us-email-anchor" href="mailto:press@autolist.com">
              {t('contentDashboard.team')}
            </a>
            .
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .contact-us {
            margin: 50px 0;
            text-align: center;
            font-size: 20px;
            line-height: 22px;
          }

          a.contact-us-email-anchor {
            font-size: 20px;
          }
        `}
      </style>
    </div>
  );
}

function Spacer({ height }: { height: number }): JSX.Element {
  return <div style={{ height }} />;
}

export default ContentDashboard;
